import { Suspense, useState } from 'react'
import type { ReactNode } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'

import { Icon, Page, SideNavigation } from '@loadsmart/miranda-react'
import { useSettings } from '@loadsmart/react-frontend-settings'

import logo from 'assets/images/analytics_portal_logo.svg'
import AssistantNavItem from 'assistant/ui/AssistantNavItem'
import { useAssistantFlag } from 'assistant/ui/hooks/useAssistantFlag'
import { useSession } from 'auth/ui/hooks/useSession'
import { RequestType } from 'core/domain/RequestType'
import type { PBIReport } from 'managed-analytics/domain/PBIReport'
import { useOpenDockPdfReportsAccess } from 'managed-analytics/ui/hooks/useOpenDockPdfReportsAccess'
import { getPBIReportsQueryOptions } from 'managed-analytics/use-cases/getPBIReports'

import { NavLabel, NewTag } from './NavLabel'
import ZendeskDialog from './zendesk/TryNowZendeskDialog'

export const StyledContainer = styled.div`
  height: 100vh;

  /*
    Miranda sets 200px on :host; !important is required to override that.
    "Performance Reports" plus its New tag needs 252px, so this is the next
    step up — shrink it and the tag clips.
  */
  m-side-navigation {
    --m-side-navigation-width: 256px !important;
  }
`

const shimmer = keyframes`
  0% {
    background-position: -240px 0;
  }
  100% {
    background-position: 240px 0;
  }
`

const SkeletonNavItem = styled.div<{ $indent?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--m-spacing-2, 8px);
  padding: var(--m-spacing-2, 8px) var(--m-spacing-4, 16px);
  padding-left: ${({ $indent }) => ($indent ? 'var(--m-spacing-8, 32px)' : 'var(--m-spacing-4, 16px)')};
`

const SkeletonBlock = styled.div<{ $width: string }>`
  width: ${({ $width }) => $width};
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    var(--m-color-background-secondary, #eceff3) 25%,
    var(--m-color-background-tertiary, #dfe3e8) 37%,
    var(--m-color-background-secondary, #eceff3) 63%
  );
  background-size: 480px 100%;
  animation: ${shimmer} 1.4s ease infinite;
`

// Widths chosen to loosely mirror the real nav item labels so the skeleton
// reads as the same menu that is about to appear.
const MENU_SKELETON_ITEMS = ['116px', '118px', '148px', '96px', '104px']

function MenuBodySkeleton() {
  return (
    <div aria-hidden="true" data-testid="side-nav-skeleton">
      {MENU_SKELETON_ITEMS.map(width => (
        <SkeletonNavItem key={width}>
          <SkeletonBlock $width="20px" />
          <SkeletonBlock $width={width} />
        </SkeletonNavItem>
      ))}
    </div>
  )
}

function ReportsSkeleton() {
  return (
    <div aria-hidden="true" data-testid="side-nav-reports-skeleton">
      <SkeletonNavItem>
        <SkeletonBlock $width="88px" />
      </SkeletonNavItem>
      <SkeletonNavItem $indent>
        <SkeletonBlock $width="132px" />
      </SkeletonNavItem>
      <SkeletonNavItem $indent>
        <SkeletonBlock $width="108px" />
      </SkeletonNavItem>
    </div>
  )
}

export type AppLayoutProps = {
  children: ReactNode
}

export default function AppLayout({ children }: AppLayoutProps) {
  const session = useSession()
  const navigate = useNavigate()
  const { isLoading: isSchedulerFlagLoading, values } = useSettings(['flags.SCHEDULER_ENABLED'])
  const isSchedulerEnabled = Boolean(values?.[0])
  const { isLoading: isReportsAccessLoading, isNavVisible: isOpenDockPdfReportsEnabled } =
    useOpenDockPdfReportsAccess()

  // The menu shape depends on the scheduler flag and on OpenDock report access.
  // Skeleton the whole nav until both are known so items settle in one paint
  // instead of popping in one by one.
  const assistantFlag = useAssistantFlag()
  const isMenuLoading = isSchedulerFlagLoading || isReportsAccessLoading || assistantFlag.isLoading

  // Signed out, the item points at the samples; an org with performance
  // reports gets its own. Signed in without access it is hidden entirely.
  const performanceReportsPath = session ? '/performance-reports' : '/demo/performance-reports'
  const isPerformanceReportsVisible = !session || isOpenDockPdfReportsEnabled

  // The customer's own data signed in, a mocked preview signed out. The
  // scheduler's flag gates the real one only; the preview is content.
  const integrationsPath = session ? '/integrations' : '/demo/integrations'
  const schedulePath = session ? '/schedule' : '/demo/schedule'
  const isScheduleVisible = !session || isSchedulerEnabled

  const [isZendeskDialogOpen, setIsZendeskDialogOpen] = useState(false)

  const location = useLocation()

  return (
    <StyledContainer>
      <Page>
        <SideNavigation>
          <SideNavigation.Header>
            <img src={logo} alt="Analytics Portal" />
          </SideNavigation.Header>
          <SideNavigation.Body>
            {session && isMenuLoading ? (
              <MenuBodySkeleton />
            ) : (
              <>
                <SideNavigation.Item
                  href="/demo"
                  leading={<Icon name="graph" color="color-primary-20" />}
                  active={location.pathname === '/demo'}
                >
                  Analytics Library
                </SideNavigation.Item>
                <SideNavigation.Item
                  href="/analytics-mcp"
                  leading={<Icon name="sparkle" color="color-brand" />}
                  active={location.pathname === '/analytics-mcp'}
                >
                  <NavLabel>
                    Analytics MCP
                    <NewTag>New</NewTag>
                  </NavLabel>
                </SideNavigation.Item>
                {assistantFlag.isEnabled && (
                  <AssistantNavItem
                    active={location.pathname.startsWith('/assistant')}
                    onNavigate={() => navigate('/assistant')}
                  />
                )}
                {isPerformanceReportsVisible && (
                  <SideNavigation.Item
                    href={performanceReportsPath}
                    leading={<Icon name="download" color="color-brand" />}
                    active={location.pathname === performanceReportsPath}
                    onClick={event => {
                      event.preventDefault()
                      navigate(performanceReportsPath)
                    }}
                  >
                    <NavLabel>
                      Performance Reports
                      <NewTag>New</NewTag>
                    </NavLabel>
                  </SideNavigation.Item>
                )}
                {/* No New tag: neither feature is new, only the preview is. */}
                <SideNavigation.Item
                  href={integrationsPath}
                  leading={<Icon name="signal" color="color-primary-20" />}
                  active={location.pathname === integrationsPath}
                  onClick={event => {
                    event.preventDefault()
                    navigate(integrationsPath)
                  }}
                >
                  Integrations
                </SideNavigation.Item>
                {isScheduleVisible && (
                  <SideNavigation.Item
                    href={schedulePath}
                    leading={<Icon name="calendar" color="color-primary-20" />}
                    active={location.pathname === schedulePath}
                    onClick={event => {
                      event.preventDefault()
                      navigate(schedulePath)
                    }}
                  >
                    Scheduler
                  </SideNavigation.Item>
                )}
                {session && (
                  <>
                    <SideNavigation.Divider />
                    <Suspense fallback={<ReportsSkeleton />}>
                      <SideNavigationReports />
                    </Suspense>
                  </>
                )}
              </>
            )}
          </SideNavigation.Body>
          <SideNavigation.Footer>
            <SideNavigation.Item
              leading={<Icon name="chat" color="color-primary-20" />}
              onClick={() => {
                setIsZendeskDialogOpen(true)
              }}
            >
              Talk to sales
            </SideNavigation.Item>
          </SideNavigation.Footer>
        </SideNavigation>

        {children}
      </Page>

      <ZendeskDialog
        requestType={RequestType.Question}
        open={isZendeskDialogOpen}
        onClose={() => setIsZendeskDialogOpen(false)}
      />
    </StyledContainer>
  )
}

export function SideNavigationReports() {
  const location = useLocation()

  const { data } = useSuspenseQuery(getPBIReportsQueryOptions())

  const reportsByProfile = data.reduce(
    (acc, report) => {
      acc[report.profile] = [...(acc[report.profile] || []), report]
      return acc
    },
    {} as Record<string, PBIReport[]>
  )

  return Object.entries(reportsByProfile).map(([profile, reports]) => (
    <SideNavigation.SubNav key={profile} expanded>
      <SideNavigation.SubNavTitle>{profile}</SideNavigation.SubNavTitle>
      <SideNavigation.SubNavContent>
        {reports.map(report => (
          <SideNavigation.SubNavItem
            key={report.uuid}
            href={`/reports/${report.uuid}?rls_filter=${encodeURIComponent(report.rlsFilter)}&customer_type=${encodeURIComponent(report.customerType)}&role_name=${encodeURIComponent(report.roleName)}&profile=${encodeURIComponent(report.profile)}`}
            active={location.pathname.startsWith(`/reports/${report.uuid}`)}
          >
            {report.reportName}
          </SideNavigation.SubNavItem>
        ))}
      </SideNavigation.SubNavContent>
    </SideNavigation.SubNav>
  ))
}
